import React, { useEffect, useRef, useState } from "react";
import { Target, AlertTriangle, X, Play, Pause, HelpCircle } from "lucide-react";
import {
  loadPoseLandmarker,
  loadObjectDetector,
  loadImagePoseLandmarker,
} from "@/lib/mediapipeLoader";
import { PersonTracker } from "@/lib/personTracker";
import { analyzeSession } from "@/lib/repDetection";
import { assessFrameQuality } from "@/lib/frameQuality";
import { resolveCaptureWindow, needsRewind } from "@/lib/captureWindow";
import HEVCHelpModal from "./HEVCHelpModal";

// Up to 4 players — each gets a distinct color for the skeleton + UI.
// Matches the colors used in the slot pills.
const PLAYER_COLORS = ["#ff3b30", "#00e5ff", "#ffd166", "#00ff88"];
const POSE_CONNECTIONS = [
  [11, 12], [11, 13], [13, 15], [12, 14], [14, 16],
  [11, 23], [12, 24], [23, 24],
  [23, 25], [25, 27], [24, 26], [26, 28],
];

// Pad the detected person bbox before cropping for pose detection. Players
// often extend a paddle / leg slightly outside the detector's box, and a
// little padding lets the pose model see the full skeleton.
const CROP_PADDING = 0.18;
// Reusable crop canvas (allocated once)
const CROP_SIZE = 256;

/**
 * Multi-player pose tracking canvas (pickleball doubles).
 * Tap up to maxPlayers players to assign them to slots; each slot is
 * tracked through the recording with its own colored skeleton, and on
 * Stop we return one analyzeSession() result per player.
 */
export default function MultiPlayerPoseCanvas({
  onStop,
  mode = "live",
  videoSrc = null,
  sport = "pickleball",
  maxPlayers = 4,
  trimStart = 0,
  trimEnd = null,
  scrubTime = null,
  onReady,
  onStream,
}) {
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const animationRef = useRef(null);
  const landmarkerRef = useRef(null);            // legacy single-shot, kept for compat / fallback
  const detectorRef = useRef(null);              // EfficientDet person detector (stage 1)
  const imagePoseRef = useRef(null);             // PoseLandmarker in IMAGE mode for per-crop (stage 3)
  const trackerRef = useRef(null);               // PersonTracker (stage 2)
  const cropCanvasRef = useRef(null);            // reusable canvas for crop extraction
  const startedAtRef = useRef(null);
  const runningRef = useRef(false);
  const lastTracksRef = useRef([]);              // [{id, bbox, lm, confidence, ...}]
  // Per-slot tracking refs.
  // playersRef.current = [{ slot, color, trackerId, frames, lastSeenAt }]
  const playersRef = useRef([]);
  const [playerCount, setPlayerCount] = useState(0);
  const [running, setRunning] = useState(false);
  const [duration, setDuration] = useState(0);
  const [status, setStatus] = useState("idle"); // 'idle' | 'loading' | 'ready' | 'running' | 'analyzing'
  const [error, setError] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [personCount, setPersonCount] = useState(0);
  const [videoPaused, setVideoPaused] = useState(true);
  const [frameQuality, setFrameQuality] = useState({ level: "good", issues: [] });
  const lastQualityUpdateRef = useRef(0);
  // Source video aspect — set from live stream's first frame or upload metadata
  // so portrait clips don't get crammed into a 16:9 box.
  const [videoAspect, setVideoAspect] = useState(null);

  /* ---------- init ---------- */
  useEffect(() => {
    let cancelled = false;
    async function init() {
      try {
        setStatus("loading");
        // 2-stage pipeline for distance + occlusion robustness:
        //   1) EfficientDet person detector finds all bodies at full frame
        //   2) PersonTracker maintains persistent IDs across frames
        //   3) Per-crop PoseLandmarker (IMAGE mode) gets high-quality
        //      landmarks for each tracked player
        // Load in parallel — they all warm up over CDN.
        const [detector, imagePose, fallbackLm] = await Promise.all([
          loadObjectDetector(8),
          loadImagePoseLandmarker(),
          loadPoseLandmarker(8, "heavy"), // fallback if detector path errors
        ]);
        if (cancelled) return;
        detectorRef.current = detector;
        imagePoseRef.current = imagePose;
        landmarkerRef.current = fallbackLm;
        trackerRef.current = new PersonTracker();
        // Reusable canvas for crop extraction (re-sized per crop)
        cropCanvasRef.current = document.createElement("canvas");

        if (mode === "live") {
          const stream = await navigator.mediaDevices.getUserMedia({
            // Bump the ideal resolution to 1920x1080 — distant players need
            // every pixel they can get for the pose landmarker to lock on.
            video: {
              facingMode: "environment",
              width: { ideal: 1920 },
              height: { ideal: 1080 },
            },
            audio: false,
          });
          if (cancelled) return;
          const v = videoRef.current;
          v.srcObject = stream;
          await v.play();
          // Surface the active stream so the parent can wire a MediaRecorder
          // for clip saving without re-acquiring camera permission.
          if (typeof onStream === "function") {
            try { onStream(stream); } catch { /* non-fatal */ }
          }
        } else if (mode === "upload" && videoSrc) {
          const v = videoRef.current;
          v.src = videoSrc;
          v.loop = false;
          v.muted = true;
          v.playsInline = true;
          // Wait for metadata OR surface a clear error if the codec/format
          // can't be decoded (common for iPhone HEVC .mov clips that Safari
          // intermittently rejects). Without this, the native <video>
          // element silently displays "Load failed" text and we hang.
          await new Promise((resolve, reject) => {
            if (v.readyState >= 1) return resolve();
            const onMeta = () => { cleanup(); resolve(); };
            const onErr = () => {
              cleanup();
              const code = v.error?.code;
              const msg =
                code === 4 ? "This clip's format isn't supported by your browser. Try re-exporting as H.264 MP4."
                : code === 3 ? "Video decoding failed. The file may be corrupted or recorded in an unsupported codec."
                : "Couldn't load the video.";
              reject(new Error(msg));
            };
            const onTimeout = () => {
              cleanup();
              reject(new Error("Video took too long to load — try a smaller clip or re-export as MP4."));
            };
            const t = setTimeout(onTimeout, 30000);
            function cleanup() {
              clearTimeout(t);
              v.removeEventListener("loadedmetadata", onMeta);
              v.removeEventListener("error", onErr);
            }
            v.addEventListener("loadedmetadata", onMeta, { once: true });
            v.addEventListener("error", onErr, { once: true });
          });
          try {
            await v.play();
            v.currentTime = trimStart && trimStart > 0
              ? trimStart
              : Math.min(0.5, (v.duration || 1) * 0.05);
            await new Promise((r) => {
              const onSeeked = () => { v.removeEventListener("seeked", onSeeked); r(); };
              v.addEventListener("seeked", onSeeked);
              setTimeout(r, 600);
            });
            v.pause();
          } catch { /* autoplay blocked */ }
          setVideoPaused(v.paused);
        }
        setStatus("ready");
        // Notify parent of duration so trim slider can render
        if (typeof onReady === "function") {
          try {
            const d = videoRef.current?.duration;
            onReady({ duration: Number.isFinite(d) ? d : undefined });
          } catch { /* */ }
        }
        tick();
      } catch (e) {
        console.error("[MultiPlayerPoseCanvas] init error", e);
        setError(e.message || "Failed to start capture");
        setStatus("idle");
      }
    }
    init();
    return () => {
      cancelled = true;
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      const v = videoRef.current;
      if (v?.srcObject) {
        v.srcObject.getTracks().forEach((t) => t.stop());
        v.srcObject = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, videoSrc]);

  /* ---------- play/pause sync ---------- */
  useEffect(() => {
    if (mode !== "upload") return;
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => setVideoPaused(false);
    const onPause = () => setVideoPaused(true);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    setVideoPaused(v.paused);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
    };
  }, [mode, videoSrc]);

  /* ---------- Detect source aspect (live + upload) ---------- */
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const readAspect = () => {
      const w = v.videoWidth, h = v.videoHeight;
      if (w > 0 && h > 0) {
        const a = w / h;
        setVideoAspect((cur) => (cur && Math.abs(cur - a) < 0.01 ? cur : a));
      }
    };
    readAspect();
    v.addEventListener("loadedmetadata", readAspect);
    v.addEventListener("resize", readAspect);
    return () => {
      v.removeEventListener("loadedmetadata", readAspect);
      v.removeEventListener("resize", readAspect);
    };
  }, [mode, videoSrc]);

  /* ---------- Persistent video error handler ----------
     iPhone HEVC .mov clips sometimes decode metadata fine but fail when
     actually trying to render frames. The native <video> element shows
     "Load failed" text on top of the canvas in that case. We catch the
     error event and surface our own overlay so the user gets a clear
     next-step instead of staring at Safari's generic message. */
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onErr = () => {
      const code = v.error?.code;
      const msg =
        code === 4 ? "This clip's format isn't supported by your browser. Try re-exporting as H.264 MP4 (most editors offer this)."
        : code === 3 ? "Video decoding failed. The file may be HEVC/H.265 — re-export as H.264 MP4 and try again."
        : "The video can't be played. Try a different clip.";
      setError(msg);
    };
    v.addEventListener("error", onErr);
    return () => v.removeEventListener("error", onErr);
  }, [videoSrc, mode]);

  /* ---------- Trim-handle scrub: seek when scrubTime changes ---------- */
  const pendingScrubRef = useRef(null);
  useEffect(() => {
    if (mode !== "upload") return;
    if (scrubTime == null || !Number.isFinite(scrubTime)) return;
    if (runningRef.current) return;
    pendingScrubRef.current = scrubTime;
    const v = videoRef.current;
    if (!v) return;
    const applySeek = () => {
      const target = pendingScrubRef.current;
      if (target == null) return;
      if (!Number.isFinite(v.duration) || v.duration <= 0) return;
      try {
        const t = Math.max(0, Math.min(v.duration - 0.05, target));
        if (Math.abs((v.currentTime || 0) - t) > 0.03) {
          v.currentTime = t;
          // Force a frame paint (Safari/Brave leave the buffer stale on
          // paused-seek). See PoseCanvas applySeek for the rationale.
          if (v.paused) {
            const pp = v.play();
            if (pp && typeof pp.then === "function") {
              pp.then(() => { try { v.pause(); } catch { /* */ } })
                .catch(() => { /* AbortError — expected */ });
            }
          }
        }
        pendingScrubRef.current = null;
      } catch { /* */ }
    };
    applySeek();
    if (pendingScrubRef.current != null) {
      const onReady = () => {
        v.removeEventListener("loadedmetadata", onReady);
        v.removeEventListener("canplay", onReady);
        applySeek();
      };
      v.addEventListener("loadedmetadata", onReady);
      v.addEventListener("canplay", onReady);
      return () => {
        v.removeEventListener("loadedmetadata", onReady);
        v.removeEventListener("canplay", onReady);
      };
    }
  }, [scrubTime, mode]);

  /* ---------- Auto-stop at trim end during recording ---------- */
  useEffect(() => {
    if (mode !== "upload") return;
    if (!running) return;
    if (trimEnd == null || !Number.isFinite(trimEnd)) return;
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      if (!v) return;
      if (v.currentTime >= trimEnd && runningRef.current) {
        v.removeEventListener("timeupdate", onTime);
        try { v.pause(); } catch { /* */ }
        // Defer to next tick so frames buffer finalizes
        setTimeout(() => { if (runningRef.current) stop(); }, 0);
      }
    };
    v.addEventListener("timeupdate", onTime);
    return () => { v.removeEventListener("timeupdate", onTime); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, trimEnd, mode]);

  /* ---------- helper: extract a crop from the video into our scratch canvas
       and return both the canvas + the source rect so we can map landmarks
       back to frame coordinates. */
  function cropFromVideo(v, bboxNorm) {
    const cropCanvas = cropCanvasRef.current;
    if (!cropCanvas) return null;
    const vw = v.videoWidth;
    const vh = v.videoHeight;
    if (!vw || !vh) return null;

    // Pad the bbox by CROP_PADDING and clamp to frame
    const padW = bboxNorm.w * CROP_PADDING;
    const padH = bboxNorm.h * CROP_PADDING;
    const sx0 = Math.max(0, bboxNorm.x - padW) * vw;
    const sy0 = Math.max(0, bboxNorm.y - padH) * vh;
    const sx1 = Math.min(1, bboxNorm.x + bboxNorm.w + padW) * vw;
    const sy1 = Math.min(1, bboxNorm.y + bboxNorm.h + padH) * vh;
    const sw = sx1 - sx0;
    const sh = sy1 - sy0;
    if (sw < 8 || sh < 8) return null;

    cropCanvas.width = CROP_SIZE;
    cropCanvas.height = CROP_SIZE;
    const cctx = cropCanvas.getContext("2d");
    cctx.fillStyle = "#000";
    cctx.fillRect(0, 0, CROP_SIZE, CROP_SIZE);
    // Maintain aspect ratio — letterbox into the square
    const scale = Math.min(CROP_SIZE / sw, CROP_SIZE / sh);
    const dw = sw * scale;
    const dh = sh * scale;
    const dx = (CROP_SIZE - dw) / 2;
    const dy = (CROP_SIZE - dh) / 2;
    cctx.drawImage(v, sx0, sy0, sw, sh, dx, dy, dw, dh);
    return {
      canvas: cropCanvas,
      sourceRect: { x: sx0 / vw, y: sy0 / vh, w: sw / vw, h: sh / vh },
      letterbox: { dx, dy, dw, dh },
    };
  }

  /* ---------- helper: map crop-space landmarks back to FRAME coords ---------- */
  function unmapLandmarks(lm, crop) {
    // lm coords are 0..1 within the CROP_SIZE square.
    // Convert to crop pixel → undo letterbox → source rect pixels → normalized frame.
    const { dx, dy, dw, dh } = crop.letterbox;
    const { x: sx, y: sy, w: sw, h: sh } = crop.sourceRect;
    return lm.map((p) => {
      if (!p) return null;
      const cropPx = p.x * CROP_SIZE;
      const cropPy = p.y * CROP_SIZE;
      // back into source-rect pixel coords
      const srcLocalX = (cropPx - dx) / (dw || 1);
      const srcLocalY = (cropPy - dy) / (dh || 1);
      return {
        x: sx + srcLocalX * sw,
        y: sy + srcLocalY * sh,
        z: p.z,
        visibility: p.visibility,
      };
    });
  }

  function tick() {
    const detector = detectorRef.current;
    const imagePose = imagePoseRef.current;
    const tracker = trackerRef.current;
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!detector || !imagePose || !tracker || !v || !c) {
      animationRef.current = requestAnimationFrame(tick);
      return;
    }
    if (v.readyState >= 2 && v.videoWidth > 0) {
      try {
        c.width = v.videoWidth;
        c.height = v.videoHeight;
        const now = performance.now();

        // ---- Stage 1: person detection at full frame ----
        const detResult = detector.detectForVideo(v, now);
        const detections = (detResult?.detections || []).map((d) => {
          const b = d.boundingBox;
          // MediaPipe returns ORIGIN_X/Y + WIDTH/HEIGHT in pixels
          return {
            x: b.originX / v.videoWidth,
            y: b.originY / v.videoHeight,
            w: b.width / v.videoWidth,
            h: b.height / v.videoHeight,
            confidence: d.categories?.[0]?.score || 0,
          };
        }).filter((b) => b.w > 0.01 && b.h > 0.02);

        // ---- Stage 2: tracker ----
        const tracks = tracker.update(detections);
        setPersonCount(tracks.length);

        // ---- Stage 3: per-crop pose for slots that are locked + a small
        //               number of unlocked tracks (so user has poses to tap).
        //               Limit total per-frame to keep us at 30fps.
        const players = playersRef.current;
        const locked = new Set(players.map((p) => p.trackerId).filter(Boolean));
        // Order: locked tracks first, then highest-confidence others
        const orderedTracks = [
          ...tracks.filter((t) => locked.has(t.id)),
          ...tracks
            .filter((t) => !locked.has(t.id))
            .sort((a, b) => (b.confidence || 0) - (a.confidence || 0)),
        ].slice(0, 6);

        const enriched = orderedTracks.map((t) => {
          // Only run pose on tracks that have a matched detection this frame
          // OR have been lost for very few frames (use last-known crop).
          if (t.lost > 3) return { ...t, lm: null };
          const crop = cropFromVideo(v, t.bbox);
          if (!crop) return { ...t, lm: null };
          try {
            const cropResult = imagePose.detect(crop.canvas);
            const lm = cropResult?.landmarks?.[0];
            if (!lm) return { ...t, lm: null };
            return { ...t, lm: unmapLandmarks(lm, crop) };
          } catch {
            return { ...t, lm: null };
          }
        });
        lastTracksRef.current = enriched;

        // Throttled frame-quality check on locked poses only
        const nowQ = performance.now();
        if (nowQ - lastQualityUpdateRef.current > 500) {
          lastQualityUpdateRef.current = nowQ;
          const lockedPoses = enriched
            .filter((t) => locked.has(t.id) && t.lm)
            .map((t) => t.lm);
          setFrameQuality(assessFrameQuality(lockedPoses, { sport }));
        }

        // Update each slot's lastSeenAt + record frame if running
        if (runningRef.current && startedAtRef.current != null) {
          const tSec = (Date.now() - startedAtRef.current) / 1000;
          for (const p of players) {
            const myTrack = enriched.find((t) => t.id === p.trackerId);
            p.frames.push({ t: tSec, lm: myTrack?.lm || null });
            if (myTrack?.lm) p.lastSeenAt = performance.now() / 1000;
          }
        }

        drawScene(enriched);
      } catch (e) {
        console.warn("[MultiPlayerPoseCanvas] detect error", e);
      }
    }
    animationRef.current = requestAnimationFrame(tick);
  }

  function drawScene(tracks) {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const w = c.width;
    const h = c.height;
    ctx.clearRect(0, 0, w, h);
    const players = playersRef.current;

    tracks.forEach((t) => {
      const owner = players.find((p) => p.trackerId === t.id);
      const color = owner ? owner.color : "rgba(255,255,255,0.40)";
      const isLost = t.lost > 0;

      // Bounding box (faint for unlocked, bold + colored for owned slots)
      ctx.strokeStyle = color;
      ctx.lineWidth = owner ? 2.5 : 1;
      ctx.setLineDash(isLost ? [4, 4] : []);
      ctx.strokeRect(t.bbox.x * w, t.bbox.y * h, t.bbox.w * w, t.bbox.h * h);
      ctx.setLineDash([]);

      // Slot label tag at top-left of bbox
      if (owner) {
        ctx.fillStyle = color;
        ctx.fillRect(t.bbox.x * w, t.bbox.y * h - 18, 36, 18);
        ctx.fillStyle = "#000";
        ctx.font = "bold 11px sans-serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "middle";
        ctx.fillText(`P${owner.slot + 1}`, t.bbox.x * w + 4, t.bbox.y * h - 9);
      } else {
        // Unowned tracks get a small "#id" tag so user can see what's available
        ctx.fillStyle = "rgba(255,255,255,0.85)";
        ctx.font = "10px sans-serif";
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        ctx.fillText(`#${t.id}`, t.bbox.x * w + 3, t.bbox.y * h + 3);
      }

      // Skeleton — only render if pose detected this frame
      if (t.lm) {
        ctx.strokeStyle = color;
        ctx.lineWidth = owner ? 2.5 : 1.5;
        ctx.shadowColor = owner ? color : "transparent";
        ctx.shadowBlur = owner ? 6 : 0;
        for (const [a, b] of POSE_CONNECTIONS) {
          const pa = t.lm[a]; const pb = t.lm[b];
          if (!pa || !pb) continue;
          ctx.beginPath();
          ctx.moveTo(pa.x * w, pa.y * h);
          ctx.lineTo(pb.x * w, pb.y * h);
          ctx.stroke();
        }
        ctx.shadowBlur = 0;
      }
    });
  }

  /* ---------- click → assign or remove player ----------
     Now pins the slot to a TRACKER ID rather than a position. The slot
     follows that tracker through occlusions + crossovers. */
  function handleClick(e) {
    if (running) return;
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const click = {
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top) / rect.height,
    };
    const tracks = lastTracksRef.current;
    if (!tracks.length) return;
    // Find the track whose bbox contains the click (or closest centroid)
    let bestTrack = null;
    let bestD = Infinity;
    for (const t of tracks) {
      const inside =
        click.x >= t.bbox.x && click.x <= t.bbox.x + t.bbox.w &&
        click.y >= t.bbox.y && click.y <= t.bbox.y + t.bbox.h;
      const cx = t.bbox.x + t.bbox.w / 2;
      const cy = t.bbox.y + t.bbox.h / 2;
      const d = Math.hypot(cx - click.x, cy - click.y);
      if (inside) { bestTrack = t; bestD = 0; break; }
      if (d < bestD) { bestD = d; bestTrack = t; }
    }
    if (!bestTrack || bestD > 0.25) return;

    // If this track is already owned by a player → toggle off
    const owner = playersRef.current.find((p) => p.trackerId === bestTrack.id);
    if (owner) {
      playersRef.current = playersRef.current.filter((q) => q !== owner);
      // Re-slot remaining players
      playersRef.current.forEach((q, idx) => { q.slot = idx; q.color = PLAYER_COLORS[idx]; });
      setPlayerCount(playersRef.current.length);
      return;
    }
    if (playersRef.current.length >= maxPlayers) return;
    const slot = playersRef.current.length;
    playersRef.current.push({
      slot,
      color: PLAYER_COLORS[slot],
      trackerId: bestTrack.id,
      frames: [],
      lastSeenAt: performance.now() / 1000,
    });
    setPlayerCount(playersRef.current.length);
  }

  function clearAll() {
    if (running) return;
    playersRef.current = [];
    setPlayerCount(0);
  }

  /* ---------- play/pause helper ---------- */
  async function togglePlay() {
    const v = videoRef.current;
    if (!v) return;
    try { if (v.paused) await v.play(); else v.pause(); } catch { /* */ }
  }

  /* ---------- start / stop ---------- */
  function start() {
    if (playersRef.current.length === 0) {
      setError("Tap each player to lock them in before starting.");
      return;
    }
    // Validate the analysis window before committing to a run — a degenerate
    // window can't produce frames, and would save as an empty "0s duration"
    // session for every tracked player.
    let win = null;
    if (mode === "upload") {
      win = resolveCaptureWindow({
        duration: videoRef.current?.duration,
        trimStart,
        trimEnd,
      });
      if (!win.ok) {
        setError(win.error);
        return;
      }
    }
    setError(null);
    for (const p of playersRef.current) p.frames = [];
    startedAtRef.current = Date.now();
    runningRef.current = true;
    setRunning(true);
    setStatus("running");
    if (mode === "upload") {
      const v = videoRef.current;
      try {
        // Always begin at the window start — the playhead is usually parked on
        // a trim handle the user just dragged, i.e. at/near the window end.
        // Starting there ends playback immediately and captures no frames.
        if (needsRewind(v.currentTime, win.start)) v.currentTime = win.start;
        v.play().catch(() => {});
      } catch { /* */ }
    }
  }

  /* ---------- duration tick ---------- */
  useEffect(() => {
    if (!running) return;
    const i = setInterval(() => {
      setDuration(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 500);
    return () => clearInterval(i);
  }, [running]);

  async function stop() {
    runningRef.current = false;
    setRunning(false);
    setStatus("analyzing");
    const v = videoRef.current;
    if (v && mode === "upload" && !v.paused) v.pause();
    const playerSummaries = playersRef.current.map((p) => {
      const detected = p.frames.filter((f) => f.lm).length;
      const dur = p.frames.length > 1
        ? p.frames[p.frames.length - 1].t - p.frames[0].t
        : 0;
      let analysis;
      try {
        analysis = analyzeSession(p.frames, sport);
      } catch (e) {
        console.error("[MultiPlayerPoseCanvas] analyze error for slot", p.slot, e);
        analysis = { sport, rep_count: 0, reps: [], no_reps_detected: true };
      }
      return {
        slot: p.slot,
        color: p.color,
        duration_seconds: +dur.toFixed(2),
        frames_processed: p.frames.length,
        frames_detected: detected,
        analysis,
      };
    });
    setStatus("ready");
    if (onStop) onStop({ players: playerSummaries });
  }

  return (
    <div className="space-y-3">
      {/* Court-framing tip — surfaces only at the start so coaches don't see
          it every time. Critical to remind that mediapipe's pose detection
          degrades sharply for players smaller than ~80px tall in frame. */}
      {!running && playersRef.current.length === 0 && (
        <div
          className="text-[11px] text-[#A3A3A3] leading-relaxed border-l-2 border-[#38BDF8]/60 pl-3 py-1"
          data-testid="court-framing-tip"
        >
          <span className="text-[#38BDF8] font-bold uppercase tracking-widest text-[10px]">2-stage tracking</span>{" "}
          — boxes appear around each detected player. Tap the BOX to lock that player to a slot. Their slot stays with them through occlusions, crossovers, and brief disappearances. Numbered tags <span className="font-mono text-white/60">#id</span> show currently-tracked but unlocked people.
        </div>
      )}

      {/* Player slot pills */}
      <div className="flex flex-wrap items-center gap-2">
        {Array.from({ length: maxPlayers }).map((_, i) => {
          const p = playersRef.current[i];
          const color = PLAYER_COLORS[i];
          const filled = !!p;
          return (
            <div
              key={i}
              data-testid={`player-slot-${i + 1}`}
              className={`inline-flex items-center gap-1.5 border px-2.5 py-1 transition-all ${
                filled ? "border-current opacity-100" : "border-white/15 opacity-50"
              }`}
              style={{ color: filled ? color : "rgba(255,255,255,0.35)" }}
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: filled ? color : "rgba(255,255,255,0.3)" }}
              />
              <span className="text-[10px] uppercase tracking-widest font-display font-bold">
                P{i + 1}
              </span>
              {filled && <span className="text-[10px] font-mono">locked</span>}
            </div>
          );
        })}
        {playerCount > 0 && !running && (
          <button
            onClick={clearAll}
            className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white px-2 py-1 transition-colors ml-2"
            data-testid="clear-all-players-btn"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Canvas / Video */}
      <div
        ref={containerRef}
        className="relative bg-black border border-white/10 overflow-hidden mx-auto"
        style={{
          aspectRatio: videoAspect || 16 / 9,
          maxHeight: videoAspect && videoAspect < 1 ? "80vh" : undefined,
          width: videoAspect && videoAspect < 1 ? "auto" : "100%",
        }}
      >
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-contain"
          playsInline
          muted
        />
        <canvas
          ref={canvasRef}
          onClick={handleClick}
          className="absolute inset-0 w-full h-full object-contain"
        />

        {/* Status pills */}
        <div className="absolute top-3 left-3 flex items-center gap-2 bg-black/70 backdrop-blur px-2.5 py-1 border border-white/10">
          <span className={`w-1.5 h-1.5 rounded-full ${
            status === "running" ? "bg-[#ff3b30] animate-pulse" :
            status === "ready" ? "bg-[#00ff88]" :
            status === "analyzing" ? "bg-[#ffd166] animate-pulse" :
            "bg-zinc-500"
          }`} />
          <span className="text-[10px] uppercase tracking-widest font-display font-bold text-white">
            {status === "loading" ? "Loading model" :
              status === "running" ? `REC · ${duration}s` :
              status === "analyzing" ? "Analyzing…" :
              `Ready · ${personCount} detected`}
          </span>
        </div>

        {/* Frame quality pill */}
        {status === "ready" && personCount > 0 && (
          <div
            data-testid="frame-quality-pill"
            className={`absolute top-11 left-3 flex items-center gap-1.5 backdrop-blur px-2.5 py-1 border pointer-events-none transition-colors ${
              frameQuality.level === "good"
                ? "bg-[#00ff88]/15 border-[#00ff88]/50"
                : frameQuality.level === "fair"
                ? "bg-[#ffab00]/15 border-[#ffab00]/50"
                : "bg-[#ff3b30]/15 border-[#ff3b30]/50"
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                frameQuality.level === "good"
                  ? "bg-[#00ff88]"
                  : frameQuality.level === "fair"
                  ? "bg-[#ffab00]"
                  : "bg-[#ff3b30]"
              }`}
            />
            <span
              className={`text-[10px] uppercase tracking-widest font-display font-bold ${
                frameQuality.level === "good"
                  ? "text-[#00ff88]"
                  : frameQuality.level === "fair"
                  ? "text-[#ffab00]"
                  : "text-[#ff3b30]"
              }`}
            >
              {frameQuality.level === "good"
                ? "Frame: good"
                : frameQuality.issues[0] || `Frame: ${frameQuality.level}`}
            </span>
          </div>
        )}

        {/* Tap-to-pick instructions */}
        {!running && status === "ready" && !error && playerCount < maxPlayers && (
          <div className="absolute top-12 left-1/2 -translate-x-1/2 max-w-[88%] bg-black/85 backdrop-blur border border-[#ff3b30]/50 px-3 py-1.5 text-center pointer-events-none flex items-center gap-2">
            <Target className="w-3.5 h-3.5 text-[#ff3b30] flex-shrink-0" />
            <span className="text-[10px] uppercase tracking-widest font-display font-bold text-white">
              Tap player {playerCount + 1} of up to {maxPlayers}
            </span>
          </div>
        )}

        {/* Play/Pause overlay */}
        {mode === "upload" && !running && status === "ready" && !error && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); togglePlay(); }}
            className="absolute bottom-3 left-3 inline-flex items-center gap-2 bg-black/70 backdrop-blur hover:bg-black/90 border border-white/30 px-3 py-2 transition-colors"
            data-testid="mp-play-pause-btn"
          >
            {videoPaused ? <Play className="w-4 h-4 text-white" fill="white" /> : <Pause className="w-4 h-4 text-white" fill="white" />}
            <span className="text-[11px] font-display uppercase tracking-widest font-bold text-white">
              {videoPaused ? "Play" : "Pause"}
            </span>
          </button>
        )}

        {error && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black text-center p-6 gap-3" data-testid="mp-error-overlay">
            <p className="text-sm text-red-400 max-w-md">{error}</p>
            {mode === "upload" && (
              <>
                <p className="text-[11px] text-[#A3A3A3] max-w-md leading-relaxed">
                  iPhone-recorded clips are usually HEVC/H.265 by default. On iOS go to Settings → Camera → Formats → choose "Most Compatible" before recording, or re-export your existing clip from Photos as MP4.
                </p>
                <button
                  type="button"
                  onClick={() => setHelpOpen(true)}
                  className="mt-1 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#38BDF8] hover:text-white font-bold border border-[#38BDF8]/40 hover:border-white/40 px-3 py-1.5 transition-colors"
                  data-testid="mp-why-failed-btn"
                >
                  <HelpCircle size={11} /> Why this clip failed
                </button>
              </>
            )}
          </div>
        )}
      </div>
      <HEVCHelpModal open={helpOpen} onOpenChange={setHelpOpen} />

      {/* Capture buttons */}
      <div className="flex gap-3">
        {!running ? (
          <button
            onClick={start}
            disabled={playerCount === 0 || status !== "ready"}
            data-testid="mp-start-btn"
            className="flex-1 bg-[#ff3b30] hover:bg-[#ff5c53] disabled:opacity-40 disabled:cursor-not-allowed text-white font-display uppercase tracking-widest text-sm font-bold py-3 transition-colors"
          >
            Start match capture {playerCount > 0 ? `· ${playerCount} player${playerCount > 1 ? "s" : ""}` : ""}
          </button>
        ) : (
          <button
            onClick={stop}
            data-testid="mp-stop-btn"
            className="flex-1 bg-zinc-800 hover:bg-zinc-700 border border-[#ff3b30] text-[#ff3b30] font-display uppercase tracking-widest text-sm font-bold py-3 transition-colors"
          >
            Stop & analyze · {duration}s
          </button>
        )}
      </div>
    </div>
  );
}
